// shop-frontend/src/pages/ShopMallDeals.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE, getMyShopProducts } from "../utils/api";
import "../styles/shopMall.css";

/* =========================
   Helpers
   ========================= */
function s(v) {
  return String(v || "").trim();
}

function normalizeBase(u) {
  const x = s(u);
  if (!x) return "https://moondala-one.onrender.com";
  return x.endsWith("/") ? x.slice(0, -1) : x;
}

function absUrl(u) {
  const x = s(u);
  if (!x) return "";
  if (x.startsWith("http://") || x.startsWith("https://")) return x;
  const base = normalizeBase(API_BASE);
  if (x.startsWith("/")) return base + x;
  return base + "/" + x;
}

function num(v, fallback = 0) {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
}

function norm(v) {
  return s(v).toLowerCase();
}

function pickTitle(p) {
  return s(p?.title || p?.name || "Untitled");
}

function pickImage(p) {
  const raw =
    s(p?.image) ||
    s(p?.imageUrl) ||
    s(p?.photo) ||
    (Array.isArray(p?.images) ? s(p.images[0]) : "") ||
    (Array.isArray(p?.media?.images) ? s(p.media.images[0]) : "") ||
    s(p?.thumbnail) ||
    "";
  return raw ? absUrl(raw) : "";
}

function pickCurrency(p) {
  return s(p?.currency || "USD").toUpperCase() || "USD";
}

function pickPrice(p) {
  // supports localPrice/price/salePrice
  return num(p?.localPrice ?? p?.price ?? p?.salePrice ?? 0, 0);
}

function pickCompareAt(p) {
  return num(p?.compareAtPrice ?? p?.compare_price ?? p?.compareAt ?? 0, 0);
}

function calcDiscountPercent(price, compareAt, stored) {
  const storedN = num(stored, 0);
  if (storedN > 0) return Math.round(storedN);
  if (compareAt > 0 && price > 0 && compareAt > price) {
    return Math.round(((compareAt - price) / compareAt) * 100);
  }
  return 0;
}

function isDeal(p) {
  const price = pickPrice(p);
  const compareAt = pickCompareAt(p);
  const discountStored = num(p?.discountPercent ?? p?.discount ?? 0, 0);

  const inferredDiscount = calcDiscountPercent(price, compareAt, discountStored);

  return (
    p?.onSale === true ||
    p?.isDeal === true ||
    discountStored > 0 ||
    inferredDiscount > 0 ||
    (compareAt > 0 && price > 0 && compareAt > price)
  );
}

function normalizeProductsPayload(x) {
  if (Array.isArray(x)) return x;
  if (Array.isArray(x?.products)) return x.products;
  if (Array.isArray(x?.items)) return x.items;
  if (Array.isArray(x?.data?.products)) return x.data.products;
  if (Array.isArray(x?.data)) return x.data;
  return [];
}

/* =========================
   Component
   ========================= */
export default function ShopMallDeals() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getMyShopProducts();
      const list = normalizeProductsPayload(data);
      const deals = (Array.isArray(list) ? list : []).filter(isDeal);
      setItems(deals);
    } catch (e) {
      setErr(e?.message || "Failed to load deals");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const query = norm(q);
    if (!query) return items;

    return (items || []).filter((p) => {
      const title = norm(p?.title || p?.name);
      const desc = norm(p?.description);
      const cat = norm(p?.categoryLabel || p?.category || p?.categoryId);
      return title.includes(query) || desc.includes(query) || cat.includes(query);
    });
  }, [items, q]);

  return (
    <div className="sm-wrap">
      <div className="sm-container">
        <div className="sm-head">
          <div style={{ minWidth: 0 }}>
            <div className="sm-title">🔥 Deals</div>
            <div className="sm-sub">
              Showing <b>{visible.length}</b> deal(s)
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="sm-btn" onClick={() => nav("/shop/mall/edit")} type="button">
              ← Back to Mall
            </button>

            <button className="sm-btn" onClick={() => nav("/shop/add-product")} type="button">
              ➕ Add Product
            </button>

            <button className="sm-btn" onClick={load} disabled={loading} type="button">
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="sm-card" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input
              className="sm-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search deals..."
            />
            <div className="sm-sub" style={{ marginTop: 0 }}>
              Tip: products appear here if they have <b>compareAtPrice</b>, <b>discountPercent</b>, or <b>onSale</b>.
            </div>
          </div>
        </div>

        {err ? (
          <div className="sm-card">
            <div className="sm-title" style={{ fontSize: 16 }}>
              Error
            </div>
            <div className="sm-sub">{err}</div>
          </div>
        ) : null}

        {loading ? (
          <div className="sm-card">
            <div className="sm-sub">Loading deals...</div>
          </div>
        ) : visible.length === 0 ? (
          <div className="sm-card">
            <div className="sm-sub">
              No deals found yet.
              <div style={{ marginTop: 8 }}>
                Tip: set <b>compareAtPrice</b> higher than <b>price</b>, or set <b>discountPercent</b>, or <b>onSale</b>.
              </div>
            </div>
          </div>
        ) : (
          <div className="sm-grid">
            {visible.map((p) => {
              const img = pickImage(p);

              const price = pickPrice(p);
              const compareAt = pickCompareAt(p);
              const currency = pickCurrency(p);

              const discount = calcDiscountPercent(
                price,
                compareAt,
                p?.discountPercent ?? p?.discount ?? 0
              );

              const views = num(p?.views, 0);
              const qty = num(p?.quantity, 0);
              const inStock = p?.inStock !== undefined ? !!p.inStock : qty > 0;

              return (
                <div
                  key={s(p?._id || p?.id || Math.random())}
                  className="sm-prodCard"
                  onClick={() => {
                    const id = s(p?._id || p?.id);
                    if (id) nav(`/product/${encodeURIComponent(id)}`);
                  }}
                  style={{ cursor: "pointer" }}
                  title="Open product"
                >
                  <div className="sm-prodTop">
                    <div className="sm-prodTitle" title={pickTitle(p)}>
                      {pickTitle(p)}
                    </div>

                    <div className="sm-prodPills">
                      {discount > 0 ? <span className="sm-pillGreen">-{discount}%</span> : null}
                      <span className="sm-pillGreen">👁 {views}</span>
                    </div>
                  </div>

                  {img ? (
                    <div className="sm-media">
                      <img src={img} alt="" className="sm-img" />
                    </div>
                  ) : (
                    <div className="sm-noImg">No image</div>
                  )}

                  <div className="sm-prodBody">
                    <div className="sm-row">
                      <span className="sm-k">Price</span>
                      <span className="sm-v">
                        {currency} {price.toFixed(2)}
                        {compareAt > price ? (
                          <span style={{ marginLeft: 8, opacity: 0.7, textDecoration: "line-through" }}>
                            {compareAt.toFixed(2)}
                          </span>
                        ) : null}
                      </span>
                    </div>

                    <div className="sm-row">
                      <span className="sm-k">Stock</span>
                      <span className="sm-v">
                        {inStock ? `In stock · Qty ${qty}` : "Out of stock"}
                      </span>
                    </div>

                    <div className="sm-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="sm-btnMini"
                        type="button"
                        onClick={() => nav("/shop/my-products")}
                      >
                        Manage Product
                      </button>
                      <button
                        className="sm-btnMini"
                        type="button"
                        onClick={() => nav("/shop/dashboard")}
                      >
                        Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
